import { Checkbox } from '@mui/joy';
import { useSubcategoryMap } from '@/features/categories/hooks/useSubcategoryMap';
import { ChipMultiSelect } from '@/components/ChipMultiSelect';
import { RangeSlider } from '@/components/RangeSlider';
import { FiltersButton } from '@/components/FiltersButton';
import { FilterSection } from '@/components/FilterSection';
import { ALL_UNITS_OPTIONS } from '@/features/units/schema';
import { newIngredientFilters } from '@/features/ingredients/schema.js';

const RANGE_FILTERS = [
  { key: 'kcal', label: 'Kcal', unit: 'kcal' },
  { key: 'protein', label: 'Protein', unit: 'g' },
  { key: 'fat', label: 'Fat', unit: 'g' },
  { key: 'carbs', label: 'Carbs', unit: 'g' },
  { key: 'fiber', label: 'Fiber', unit: 'g' },
  { key: 'density', label: 'Density', unit: 'g/ml' },
  { key: 'piece_weight', label: 'Piece Weight', unit: 'g' },
  { key: 'slice_weight', label: 'Slice Weight', unit: 'g' },
  { key: 'package_size', label: 'Package Size', unit: 'g' },
  { key: 'portion_size', label: 'Portion Size', unit: 'g' },
];

const UNIT_OPTIONS = ALL_UNITS_OPTIONS.map(({ value, label }) => ({ name: value, label }));

export function IngredientFiltersButton({ filters, setFilters, bounds, resultsCount, sx }) {
  const { categories } = useSubcategoryMap();

  const subcategoryOptions = Array.from(
    new Set(categories.flatMap((category) => (category.subcategories || []).map((subcategory) => subcategory.name)))
  ).map((name) => ({ name }));

  // Categories are picked on the page itself, not in here, so they are neither
  // counted on the badge nor reset by "Clear all".
  const activeCount = filters.subcategories.length
    + filters.preferredUnits.length
    + (filters.commonOnly ? 1 : 0)
    + RANGE_FILTERS.filter((range) => filters[range.key] !== null).length;

  const clearAll = () => {
    const defaults = newIngredientFilters();
    setFilters({ ...filters, ...defaults, categories: filters.categories });
  };

  return (
    <FiltersButton
      activeCount={activeCount}
      onClearAll={clearAll}
      resultsCount={resultsCount}
      resultsLabel="ingredients"
      sx={sx}
    >
      <FilterSection title="Subcategory">
        <ChipMultiSelect
          value={filters.subcategories}
          onChange={(subcategories) => setFilters({ ...filters, subcategories })}
          options={subcategoryOptions}
          size="sm"
          entity="subcategories"          
        />
      </FilterSection>
      <FilterSection title="Preferred Unit">
        <ChipMultiSelect
          value={filters.preferredUnits}
          onChange={(preferredUnits) => setFilters({ ...filters, preferredUnits })}          
          options={UNIT_OPTIONS}
          size="sm"
          entity="units"          
        />
      </FilterSection>
      <Checkbox
        size="sm"
        label="Common ingredients only"
        checked={filters.commonOnly}
        onChange={(e) => setFilters({ ...filters, commonOnly: e.target.checked })}
      />
      {RANGE_FILTERS.map((range) => (
        <RangeSlider
          key={range.key}
          label={range.label}
          unit={range.unit}
          value={filters[range.key]}
          bounds={bounds[range.key]}
          onChange={(value) => setFilters({ ...filters, [range.key]: value })}
        />
      ))}
    </FiltersButton>
  );
}
